import moment from 'moment';
import { TEMPLATE_ID_DUAL_SHARE_CLASS } from './templateConstants';
import { DUAL_SHARE_PREISE_ROW_KEYS } from './preiseFields';

/** Month + year (legacy; not used for 698422-structure preview/PDF headers) */
export function formatFactsheetHeaderMonthYear(date: string | Date | undefined): string {
  if (!date) return moment().format('MMMM YYYY');
  const momentDate = moment(date);
  momentDate.locale('de');
  return momentDate.format('MMMM YYYY');
}

/** Normalize any factsheet date to GraphQL `until` (YYYY-MM-DD). */
export function formatDateToUntil(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  const raw = String(dateStr).trim();
  const isoPart = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (isoPart && moment(isoPart, 'YYYY-MM-DD', true).isValid()) return isoPart;
  const de = moment(raw, ['DD.MM.YYYY', 'D.M.YYYY'], true);
  if (de.isValid()) return de.format('YYYY-MM-DD');
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Valuation date for automated factsheet data (monthly bars, PREISE KV,
 * period/risk tables, history chart). Prefer Axion snapshot date over publish date.
 */
export function resolveFactsheetUntilIso(pdf?: {
  published_at?: string;
  historyUntil?: string;
  data_as_of?: string;
  preise?: { kursper?: string };
}): string | undefined {
  const candidates = [
    pdf?.preise?.kursper,
    pdf?.historyUntil,
    pdf?.data_as_of,
    pdf?.published_at,
  ];
  for (const raw of candidates) {
    const formatted = formatDateToUntil(raw);
    if (formatted) return formatted;
  }
  return undefined;
}

/**
 * Monthly rolling bar chart (`WERTENTWICKLUNG NACH JAHREN`) — same as published_at.
 * Do not snap mid-month published_at to prior month-end — July-anniversary funds
 * (e.g. LU0566535208) must keep until=14.07 so rolling bars use July period-ends.
 * June-fiscal funds are handled server-side via anniversary-month detection.
 */
export function resolveBarChartUntilIso(pdf?: {
  published_at?: string;
}): string | undefined {
  return resolveFactsheetUntilIso(pdf);
}

/**
 * Exact date in header.
 * Prefer `published_at` (what editors set as Published At) so Save → Preview stays consistent.
 * Fall back to valuation/CSV as-of (`kursper` / historyUntil / data_as_of) when publish date is empty.
 */
export function formatFactsheetHeaderDate(
  _templateId: string | undefined,
  publishedAt: string | Date | undefined,
  _shareClassColumns?: Array<{ preise?: Record<string, unknown> }>,
  preise?: Record<string, unknown>,
  pdfMeta?: { historyUntil?: string; data_as_of?: string },
): string {
  const candidates = [
    publishedAt,
    preise?.kursper,
    pdfMeta?.historyUntil,
    pdfMeta?.data_as_of,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const formatted = formatDateToUntil(String(raw));
    if (formatted) return moment(formatted, 'YYYY-MM-DD').format('DD.MM.YYYY');
    const m = moment(raw as string | Date);
    if (m.isValid()) return m.format('DD.MM.YYYY');
  }
  return moment().format('DD.MM.YYYY');
}

/** Keep valuation-date fields aligned when the editor changes Published At. */
export function syncValuationDatesFromPublishedAt<T extends Record<string, any>>(
  data: T,
  publishedAt: string | undefined,
): T {
  const until = formatDateToUntil(publishedAt);
  if (!until) return data;

  const nextPreise = {
    ...(data.preise && typeof data.preise === 'object' ? data.preise : {}),
    kursper: until,
  };

  return {
    ...data,
    published_at: publishedAt,
    historyUntil: until,
    data_as_of: until,
    preise: nextPreise,
  };
}

export const PREISE_DISPLAY_ROW_KEYS = [
  ['wahrung', 'Währung:'],
  ['mepreis', 'Ausgabe-/Rücknahmepreis:'],
  ['fondsvolumen', 'Fondsvolumen:'],
  ['ertragsart', 'Ertragsart:'],
  ['Letzte Ausschüttung', 'Letzte Ausschüttung:'],
  ['ausschuttung', 'Ausschüttungstermin:'],
] as const;

export type PreiseDisplayRowKey = (typeof PREISE_DISPLAY_ROW_KEYS)[number][0];

export type PreiseDisplayRow = [string, string];

/** PREISE rows in preview/PDF */
export function getPreiseDisplayRows(templateId?: string): PreiseDisplayRow[] {
  if (templateId === TEMPLATE_ID_DUAL_SHARE_CLASS) {
    return DUAL_SHARE_PREISE_ROW_KEYS.map((row) => [row[0], row[1]]);
  }
  return PREISE_DISPLAY_ROW_KEYS.map(([key, label]) => [key, label]);
}
